import React, { useRef, useCallback, useMemo } from 'react';
import { view } from '@yola/ws-sdk';
import { reactDnD } from '@yola/ws-ui';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import throttle from 'lodash.throttle';
import selectors from '../selectors';
import DragTypes from '../constants/drag-types';
import hoverHandler from '../helpers/hover-handler';
import dropHandler from '../helpers/drop-handler';

const { useDrop } = reactDnD;

function ViewDropAreaContainer() {
  const areaRef = useRef(null);

  const dragSourceElementId = useSelector(selectors.getDragSourceElement);
  const dropTargetElementId = useSelector(selectors.getDropTargetElement);
  const adjacentPosition = useSelector(selectors.getAdjacentPosition);
  const scrollPosition = useSelector(view.selectors.getScrollPosition);

  const props = useMemo(
    () => ({
      dragSourceElementId,
      dropTargetElementId,
      adjacentPosition,
      scrollPosition,
    }),
    [dragSourceElementId, dropTargetElementId, adjacentPosition, scrollPosition]
  );

  const throttledHoverHandler = useMemo(() => throttle(hoverHandler, 100), []);

  const handleHover = useCallback(
    (item, monitor) => {
      throttledHoverHandler(props, monitor, { area: areaRef.current });
    },
    [props, throttledHoverHandler]
  );

  const handleDrop = useCallback(() => {
    dropHandler(props);
  }, [props]);

  const [, drop] = useDrop(
    () => ({
      accept: DragTypes.VIEW_ELEMENT,
      hover: handleHover,
      drop: handleDrop,
    }),
    [handleHover, handleDrop]
  );

  const className = classNames('ws-viewport-droparea', {
    'ws-viewport-droparea--active': dragSourceElementId !== null,
  });

  return (
    <div
      className={className}
      ref={(node) => {
        areaRef.current = node;
        drop(node);
      }}
    />
  );
}

export default ViewDropAreaContainer;
